import Modal from '../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Select from '../../BaseComponents/Select';
import { ChangeEvent, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import {
  createHealthSummary,
  getMemberHealthSummaryList,
  updateHealthSummary,
} from '../../../api/healthSummary';
import {
  setHealthSummariesList,
  setHealthSummaryDataToEdit,
  setHealthSummaryIdToEdit,
  updateHealthSummaryStore,
} from '../../../store/memberSlice';

type IProps = {
  isOpen: boolean;
  onClose: () => void;
};

interface FormValues {
  bodySystem: { key: string; value: string };
  healthDetails: string;
}

const AppointmentModal = ({ isOpen, onClose }: IProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<boolean>(false);

  const {
    memberData,
    healthSummaryIdToEdit,
    healthSummaryDataToEdit,
    healthSummaryList,
  } = useSelector((state: RootState) => state.member);
  const { bodySystemsList } = useSelector((state: RootState) => state.app);

  const bodySystemsOptions = bodySystemsList.map((item) => ({
    key: item.id,
    value: item.title,
  }));

  const initialValues = useMemo(() => {
    return healthSummaryIdToEdit && healthSummaryDataToEdit
      ? {
          bodySystem: {
            key: `${healthSummaryDataToEdit?.body_system.id}`,
            value: healthSummaryDataToEdit?.body_system.title,
          },
          healthDetails: healthSummaryDataToEdit?.details,
        }
      : {
          bodySystem: { key: '', value: '' },
          healthDetails: '',
        };
  }, [healthSummaryIdToEdit, healthSummaryDataToEdit]);

  const formik = useFormik<FormValues>({
    initialValues,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        if (isFormError) setIsFormError(false);
        const requestObj = {
          member_id: memberData?.user_id!,
          body_system_id: +values.bodySystem.key,
          details: values.healthDetails,
        };

        const resp = healthSummaryIdToEdit
          ? await updateHealthSummary({
              ...requestObj,
              health_summary_id: healthSummaryIdToEdit,
            })
          : await createHealthSummary(requestObj);

        if (resp) {
          if (healthSummaryDataToEdit) {
            dispatch(
              updateHealthSummaryStore({
                healthSummaryIdToEdit,
                note: values.healthDetails,
              })
            );
          } else {
            const resp = await getMemberHealthSummaryList(memberData?.user_id!);
            if (resp) {
              dispatch(setHealthSummariesList(resp.health_summaries));
            }
          }

          dispatch(setHealthSummaryIdToEdit(null));
          dispatch(setHealthSummaryDataToEdit(null));
          onClose();

          formik.resetForm();
        }
      } catch (error) {
        console.error('Error sending magic link:', error);
        setIsFormError(true);
      } finally {
        setIsLoading(false);
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const handleDetailsChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (formik.errors.healthDetails && formik.submitCount > 0) {
      formik.setFieldError('healthDetails', '');
    }
    formik.handleChange(e);
  };

  const filteredBodySystemsOptions = useMemo(() => {
    const createdSummariesIds = healthSummaryList?.map(
      (item) => item.body_system.id
    );

    return bodySystemsOptions.filter(
      (item) => !createdSummariesIds?.includes(+item.key)
    );
  }, [bodySystemsOptions, healthSummaryList]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        dispatch(setHealthSummaryIdToEdit(null));
        dispatch(setHealthSummaryDataToEdit(null));
        onClose();

        formik.resetForm();
      }}
      title={'Add Health Summary'}
      memberName={`${memberData?.first_name} ${memberData?.last_name}`}
      width={'600px'}
    >
      <div className='summary-modal-container'>
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='section-title'>Body System *</div>
          <div className='body-systems-inputs-wrapper'>
            <div className='input-wrapper'>
              <Select
                name={`bodySystem`}
                placeholder={'Choose body system'}
                onChange={(option) =>
                  formik.setFieldValue('bodySystem', option)
                }
                options={filteredBodySystemsOptions}
                selectedOption={formik.values.bodySystem}
                isError={!!(formik.submitCount > 0 && formik.errors.bodySystem)}
              />
            </div>
          </div>
          <div className='section-title'>Health Details *</div>
          <TextArea
            rows={6}
            name='healthDetails'
            placeholder='Enter details'
            isError={formik.submitCount > 0 && !!formik.errors.healthDetails}
            errorMessage={
              formik.submitCount > 0 ? formik.errors.healthDetails : undefined
            }
            onChange={handleDetailsChange}
            value={formik.values.healthDetails}
          />
          <div className='btn-wrapper'>
            <Button
              text={
                healthSummaryIdToEdit ? 'Save changes' : 'Add health summary'
              }
              type='submit'
              isLoading={isLoading}
              isDisabled={
                !formik.values.bodySystem.key || !formik.values.healthDetails
              }
            />
            {isFormError && (
              <div className='error-message'>
                Something went wrong. Please try again.
              </div>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AppointmentModal;
